import Phaser from 'phaser';

import { GameBoardViewModel } from './game-board-view-model';
import { BlackjackMainScene } from '../../scenes/blackjack-main-scene';
import { StartView } from '../components/start-view';
import { GameSizeMetrics } from 'src/games/common/phaser/game/scenes/scaled-base-scene';
import { TextService } from '../../services/text-service';
import { ResizeMetrics } from '../../model/resize-metrics';
import { RoundInfoDividerView } from './round-info-divider-view';
import { PlayerBettingBoxView } from '../player/player-betting-box-view';
import { CroupierBettingBoxView } from '../croupier/croupier-betting-box-view';
import { Subscription } from 'rxjs';
import { BlackjackEvents, GameEvent } from 'game-logic';
import { CardDeckView } from './card-deck-view';
import { ResultsView } from './results-view';
import { ChipPickerView } from '../chips/picker/chip-picker-view';

export class GameBoardView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;

  private subscriptionGroup: Subscription;
  private textService: TextService;

  private startView: StartView;
  private roundInfoDividerView: RoundInfoDividerView;
  private cardDeckView: CardDeckView;
  private playerBettingBoxView: PlayerBettingBoxView;
  private croupierBettingBoxView: CroupierBettingBoxView;
  private chipPickerView: ChipPickerView;
  private resultsView: ResultsView;

  constructor(
    scene: BlackjackMainScene,
    x: number,
    y: number,
    private viewModel: GameBoardViewModel,
  ) {
    super(scene, x, y);

    this.subscriptionGroup = new Subscription();

    this.textService = new TextService(
      scene,
      this,
      viewModel.defaultFontFamily,
      viewModel.layoutConfig.availableTextWidth,
    );

    this.startView = new StartView(scene, 0, 0, viewModel.startViewModel);
    this.add(this.startView);

    this.roundInfoDividerView = new RoundInfoDividerView(
      scene,
      0,
      0,
      viewModel.roundInfoDividerViewModel,
    );
    this.add(this.roundInfoDividerView);

    this.cardDeckView = new CardDeckView(scene, 0, 0, viewModel.cardDeckViewModel);
    this.add(this.cardDeckView);

    this.playerBettingBoxView = new PlayerBettingBoxView(
      scene,
      0,
      0,
      viewModel.playerBettingBoxViewModel,
    );
    this.add(this.playerBettingBoxView);

    this.croupierBettingBoxView = new CroupierBettingBoxView(
      scene,
      0,
      0,
      viewModel.croupierBettingBoxViewModel,
    );
    this.add(this.croupierBettingBoxView);

    this.chipPickerView = new ChipPickerView(scene, 0, 0, viewModel.chipPickerViewModel);
    this.add(this.chipPickerView);

    this.resultsView = new ResultsView(scene, 0, 0, viewModel.resultsViewModel);
    this.add(this.resultsView);

    this.setName(GameBoardView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      viewModel.logicEventQueue$.subscribe((eventQueue) => {
        eventQueue?.subscribe((event) => this.handleLogicEvents(event, scene));
      }),
    );
  }

  public onResize(gameSizeMetrics: GameSizeMetrics) {
    const scalingMetrics = this.viewModel.calculateScaleFactor(gameSizeMetrics);
    const scaledFontMetrics = this.textService.refreshFontSizes(scalingMetrics.scaleFactor);

    this.currentResizeMetrics = {
      layoutConfig: this.viewModel.layoutConfig,
      sizeMetrics: gameSizeMetrics,
      scalingMetrics: scalingMetrics,
      fontMetrics: scaledFontMetrics,
    };

    this.startView?.onResize(this.currentResizeMetrics);
    this.playerBettingBoxView?.onResize(this.currentResizeMetrics);
    this.croupierBettingBoxView?.onResize(this.currentResizeMetrics);
    this.cardDeckView?.onResize(this.currentResizeMetrics);
    this.roundInfoDividerView?.onResize(this.currentResizeMetrics);
    this.chipPickerView?.onResize(this.currentResizeMetrics);
    this.resultsView?.onResize(this.currentResizeMetrics);
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(fromScene);
  }

  private async handleLogicEvents(
    event: GameEvent<BlackjackEvents>,
    _scene: BlackjackMainScene,
  ): Promise<void> {
    // NOTE: Ignore instant flag here
    switch (event.type) {
      case 'onRoundStateChanged':
        // TODO: Disable auto finish until we can create an alternative solution
        // this.viewModel.awaitAutoFinish(
        //   event.payload.state,
        //   event.payload.isOutOfChips,
        //   event.payload.isGameCompleted,
        // );
        break;
      default:
        break;
    }
  }
}
